export const heroVerifications = [
  {
    id: 'identity',
    title: 'Biometric Identity',
    description: 'Authorised by your fingerprint or face.',
    icon: 'ri-fingerprint-line',
  },
  {
    id: 'crypto',
    title: 'Cryptographic protection',
    description: 'Your message is securely bound to your identity.',
    icon: 'ri-lock-2-line',
  },
  {
    id: 'trusted',
    title: 'Trusted verification',
    description: 'Anyone can check authenticity with a compatible app.',
    icon: 'ri-shield-check-line',
  },
  {
    id: 'apps',
    title: 'Works across many apps',
    description: 'bioMail, invoices and browser extensions.',
    icon: 'ri-apps-2-line',
  },
];

export const playgroundHighlights = [
  {
    id: 'identity',
    title: 'Biometric Identity',
    description: 'Authenticates the registered person using a biometric scan.',
    icon: 'ri-fingerprint-line',
  },
  {
    id: 'crypto',
    title: 'Cryptographic protection',
    description: 'Safeguards message and verification data from tampering.',
    icon: 'ri-lock-2-line',
  },
  {
    id: 'server',
    title: 'Server-validated',
    description: 'Checks the stamp against the registered identity and content.',
    icon: 'ri-shield-check-line',
  },
  {
    id: 'qr',
    title: 'Not just a QR code',
    description: 'The QR pattern carries data — the security comes from identity, cryptography and verification.',
    icon: 'ri-qr-code-line',
  },
];

export const stampFeatures = [
  {
    id: 'identity',
    title: 'Biometric identity',
    description: 'Authorised by your fingerprint or face.',
    icon: 'ri-fingerprint-line',
  },
  {
    id: 'crypto',
    title: 'Cryptographic protection',
    description: 'Your message is securely bound to your identity.',
    icon: 'ri-lock-2-line',
  },
  {
    id: 'trusted',
    title: 'Trusted verification',
    description: 'Anyone can check the authenticity of the bioStamp using a compatible application.',
    icon: 'ri-shield-check-line',
  },
];

export const spoofStates = [
  {
    id: 'original',
    label: 'Original (valid)',
    success: true,
    statusTitle: 'Verification Successful',
    statusText: 'The bioStamp is valid and matches the registered sender and protected information.',
    rows: [
      { label: 'Registered sender', value: 'Ardeun Demo' },
      { label: 'Message integrity', value: 'Protected fields match', muted: true },
      { label: 'bioStamp', value: 'Valid' },
    ],
  },
  {
    id: 'altered',
    label: 'Altered message',
    success: false,
    statusTitle: 'Verification Failed',
    statusText: 'The protected message was changed after the bioStamp was created.',
    rows: [
      { label: 'Registered sender', value: 'Ardeun Demo' },
      { label: 'Message integrity', value: 'Tampered — mismatch' },
      { label: 'bioStamp', value: 'Valid' },
    ],
  },
  {
    id: 'replaced',
    label: 'Replaced stamp',
    success: false,
    statusTitle: 'Verification Failed',
    statusText: 'The bioStamp does not correspond to the registered sender for this message.',
    rows: [
      { label: 'Registered sender', value: 'Unknown sender' },
      { label: 'Message integrity', value: 'Protected fields match', muted: true },
      { label: 'bioStamp', value: 'Invalid' },
    ],
  },
];

export const generateApps = [
  { id: 'biomail', name: 'bioMail', description: 'Secure emails and attachments', icon: 'ri-mail-line' },
  { id: 'qbo', name: 'QBOVerifier', description: 'QuickBooks invoices', icon: 'ri-file-check-line' },
  { id: 'xero', name: 'XeroVerifier', description: 'Xero invoices', icon: 'ri-file-text-line' },
  { id: 'other', name: 'Other business applications', description: 'More integrations coming soon', icon: 'ri-add-line' },
];

export const verifyApps = [
  { id: 'web', name: 'Web Mail (Chrome/Firefox)', description: 'Gmail, Outlook Web, others', icon: 'ri-window-line' },
  { id: 'ios', name: 'iOS App', description: 'bioMail & bioVerifier', icon: 'ri-apple-line' },
  { id: 'android', name: 'Android App', description: 'bioMail & bioVerifier', icon: 'ri-android-line' },
  { id: 'desktop', name: 'Desktop Email', description: 'Outlook (Add-In), Thunderbird (Extension)', icon: 'ri-computer-line' },
];

export const featuredTech = [
  {
    id: 'send',
    title: 'Send a Verified Email',
    description: 'Send yourself an email with a bioStamp and see how verification works.',
    badge: 'Most Popular',
    badgeStyle: 'primary',
    cta: 'Try It Now',
    icon: 'ri-mail-send-line',
  },
  {
    id: 'private',
    title: 'Private bioMail',
    description: 'Person-to-Person encryption keeps your messages and attachments private.',
    badge: 'Essential',
    badgeStyle: 'accent',
    cta: 'Learn More',
    icon: 'ri-lock-2-line',
  },
  {
    id: 'invoice',
    title: 'Invoice Verification',
    description: 'XeroVerifier and QBOVerifier protect your business invoices.',
    badge: 'Essential',
    badgeStyle: 'accent',
    cta: 'View Examples',
    icon: 'ri-file-list-3-line',
  },
];

export const additionalTech = [
  {
    id: 'mobile',
    title: 'Mobile Verification',
    description: 'Use the bioMail and bioVerifier apps on Android and iOS.',
    cta: 'Learn More',
    icon: 'ri-smartphone-line',
  },
  {
    id: 'vault',
    title: 'Private Vault',
    description: 'Store sensitive messages and files locally, protected by your biometric.',
    cta: 'See How It Works',
    icon: 'ri-safe-2-line',
  },
];

export const trustSteps = [
  {
    id: 1,
    number: '1',
    title: 'Registered Sender',
    description: 'Identity authenticated with biometrics',
    icon: 'ri-user-star-line',
  },
  {
    id: 2,
    number: '2',
    title: 'Protected bioStamp',
    description: 'Cryptographically bound verification data',
    icon: 'ri-shield-keyhole-line',
  },
  {
    id: 3,
    number: '3',
    title: 'Email Delivered',
    description: 'Compatible with your email application',
    icon: 'ri-mail-check-line',
  },
  {
    id: 4,
    number: '4',
    title: 'Identity Verified',
    description: 'The recipient checks the stamp and content',
    icon: 'ri-check-double-line',
  },
];